/**
 * PR-37A — Push Subscription Layer.
 * Privacy-safe client registry that captures push opt-in state, device type
 * and platform. Stored in localStorage and emitted to analytics so the
 * backend warehouse can reconstruct opt-in funnels without storing PII.
 * Compatible with Android Chrome, Android PWA, Desktop Chrome, and Safari
 * (when Notification API exists).
 */
import { trackEvent } from "@/lib/audit";
import { getPermission, isPushSupported } from "./pushPermission";

const KEY = "vs:push:subscription:v1";

export type DeviceType = "mobile" | "tablet" | "desktop" | "unknown";
export type PushPlatform =
  | "android_chrome"
  | "android_pwa"
  | "ios_safari"
  | "ios_pwa"
  | "desktop_chrome"
  | "desktop_safari"
  | "desktop_firefox"
  | "desktop_other"
  | "unsupported";

export interface PushSubscriptionRecord {
  enabled: boolean;
  permission: NotificationPermission | "unsupported";
  deviceType: DeviceType;
  platform: PushPlatform;
  standalone: boolean;
  subscribedAt?: number;
  unsubscribedAt?: number;
}

function detectDevice(): DeviceType {
  if (typeof navigator === "undefined") return "unknown";
  const ua = navigator.userAgent.toLowerCase();
  if (/ipad|tablet/.test(ua)) return "tablet";
  if (/mobi|android|iphone|ipod/.test(ua)) return "mobile";
  return "desktop";
}

function detectStandalone(): boolean {
  if (typeof window === "undefined") return false;
  try {
    if (window.matchMedia?.("(display-mode: standalone)").matches) return true;
    const nav = navigator as Navigator & { standalone?: boolean };
    return nav.standalone === true;
  } catch { return false; }
}

function detectPlatform(): PushPlatform {
  if (typeof navigator === "undefined" || !isPushSupported()) return "unsupported";
  const ua = navigator.userAgent;
  const standalone = detectStandalone();
  const isAndroid = /Android/i.test(ua);
  const isIOS = /iPhone|iPad|iPod/i.test(ua);
  const isChrome = /Chrome\//.test(ua) && !/Edg\//.test(ua);
  const isSafari = /Safari\//.test(ua) && !/Chrome\//.test(ua);
  const isFirefox = /Firefox\//.test(ua);
  if (isAndroid) return standalone ? "android_pwa" : (isChrome ? "android_chrome" : "desktop_other");
  if (isIOS) return standalone ? "ios_pwa" : "ios_safari";
  if (isChrome) return "desktop_chrome";
  if (isSafari) return "desktop_safari";
  if (isFirefox) return "desktop_firefox";
  return "desktop_other";
}

function read(): PushSubscriptionRecord | null {
  if (typeof window === "undefined") return null;
  try { return JSON.parse(window.localStorage.getItem(KEY) ?? "null"); }
  catch { return null; }
}

function write(r: PushSubscriptionRecord): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(KEY, JSON.stringify(r));
}

export function getSubscriptionSnapshot(): PushSubscriptionRecord {
  const existing = read();
  const permission = getPermission();
  const record: PushSubscriptionRecord = existing ?? {
    enabled: false,
    permission,
    deviceType: detectDevice(),
    platform: detectPlatform(),
    standalone: detectStandalone(),
  };
  // Keep permission fresh on every read.
  record.permission = permission;
  return record;
}

/**
 * Mark the user as subscribed once the OS-level prompt resolves to "granted".
 * Pure client bookkeeping — no token storage, no PII.
 */
export function registerSubscription(): PushSubscriptionRecord {
  const snap = getSubscriptionSnapshot();
  const enabled = snap.permission === "granted";
  const record: PushSubscriptionRecord = {
    ...snap,
    enabled,
    subscribedAt: enabled ? Date.now() : snap.subscribedAt,
  };
  write(record);
  if (enabled) {
    void trackEvent("push_subscribed", {
      device: record.deviceType,
      platform: record.platform,
      standalone: record.standalone,
    });
  }
  return record;
}

export function unregisterSubscription(reason: string = "user"): void {
  const snap = getSubscriptionSnapshot();
  const record: PushSubscriptionRecord = {
    ...snap,
    enabled: false,
    unsubscribedAt: Date.now(),
  };
  write(record);
  void trackEvent("push_unsubscribed", { reason, platform: record.platform });
}

export function isPushEnabled(): boolean {
  const snap = getSubscriptionSnapshot();
  return snap.enabled && snap.permission === "granted";
}
